import * as React from 'react'
import { redirect } from 'react-router-dom'

import { ExpensesRoutesVisibilityDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation, buildRoute } from 'modules/core/lib/react-router'

import { PageDateFiltersProvider } from 'modules/expenses/lib/PageDateFiltersContext'
import { WithDateFilters } from 'modules/expenses/lib/WithDateFilters'

const MyGroupDetailPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/Groups/DetailPage')
)
const MyGroupExpenseDetailPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/Groups/DetailPage/MyGroupExpenseDetailPage')
)
const MyExpenseDetailPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/MyExpenseDetailPage')
)
const MyExpensesListTab = React.lazy(
  () => import('modules/expenses/pages/my_expenses/MyExpensesListTab')
)
const GroupEditPage = React.lazy(() => import('modules/expenses/pages/Groups/EditPage'))
const MyGroupsListTab = React.lazy(() => import('modules/expenses/pages/MyGroupsListTab'))
const ExporterPage = React.lazy(() => import('modules/expenses/pages/exporter/ExporterPage'))
const MyExpensesListPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/MyExpensesListPage')
)
const MyExpensesRootPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/MyExpensesRootPage')
)
const NewExpenseOcrModalPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/NewExpenseOcrModalPage')
)
const NewGroupModalPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/NewGroupModalPage')
)
const NewManualExpensesPage = React.lazy(
  () => import('modules/expenses/pages/my_expenses/NewManualExpensesPage')
)
const NewMileagePage = React.lazy(() => import('modules/expenses/pages/my_expenses/NewMileagePage'))
const NewPerDiemPage = React.lazy(() => import('modules/expenses/pages/my_expenses/NewPerDiemPage'))

export const myExpensesRoutes = buildNavigation((navigation) => {
  return [
    buildRoute({
      path: navigation.myExpenses.path,
      element: <MyExpensesRootPage />,
      children: [
        {
          index: true,
          loader: () => redirect(navigation.myExpenses.list.fullPath()),
        },
        {
          path: navigation.myExpenses.list.path,

          handle: {
            meta: ({ watchQuery, session }) => {
              return watchQuery({
                query: ExpensesRoutesVisibilityDocument,
                variables: { companyId: session.company.id },
              }).map(({ data }) => {
                const expensableGroupsEnabled =
                  data.expenses.expensableGroupsSettingsConnection?.nodes[0]?.enabled ?? false

                return {
                  title: i18n.t('expenses_v2.breadcrumbs.my_expenses'),
                  illustration: 'wallet',
                  subNavigation: [
                    {
                      id: 'my-expenses-list',
                      title: i18n.t('expenses_v2.list.tabs.expenses'),
                      href: navigation.myExpenses.list.expenses.fullPath(),
                    },
                    {
                      id: 'my-groups-list',
                      title: i18n.t('expenses_v2.list.tabs.groups'),
                      href: navigation.myExpenses.list.groups.fullPath(),
                      isVisible: expensableGroupsEnabled,
                    },
                  ],
                }
              })
            },
          },

          children: [
            {
              index: true,
              loader: () => redirect(navigation.myExpenses.list.expenses.fullPath()),
            },
            {
              path: navigation.myExpenses.list.expenses.path,
              children: [
                {
                  path: navigation.myExpenses.list.expenses.new.path,
                  element: <NewManualExpensesPage />,
                },
                {
                  path: navigation.myExpenses.list.expenses.newMileage.path,
                  element: <NewMileagePage />,
                },
                {
                  path: navigation.myExpenses.list.expenses.newPerDiem.path,
                  element: <NewPerDiemPage />,
                },
                {
                  path: navigation.myExpenses.list.expenses.newGroup.path,
                  element: <NewGroupModalPage />,
                },
                {
                  path: navigation.myExpenses.list.expenses.newExpenseOcr.path,
                  element: <NewExpenseOcrModalPage />,
                },

                {
                  path: navigation.myExpenses.list.expenses.exporter.path,
                  element: <ExporterPage />,
                },
                {
                  path: navigation.myExpenses.list.expenses.expenseId.path,
                  element: <MyExpenseDetailPage />,
                },
              ],
              element: <MyExpensesListTab />,
            },
            {
              path: navigation.myExpenses.list.groups.path,

              children: [
                {
                  path: navigation.myExpenses.list.groups.new.path,
                  element: <NewManualExpensesPage />,
                },
                {
                  path: navigation.myExpenses.list.groups.newMileage.path,
                  element: <NewMileagePage />,
                },
                {
                  path: navigation.myExpenses.list.groups.newPerDiem.path,
                  element: <NewPerDiemPage />,
                },
                {
                  path: navigation.myExpenses.list.groups.newGroup.path,
                  element: <NewGroupModalPage />,
                },
                {
                  path: navigation.myExpenses.list.groups.newExpenseOcr.path,
                  element: <NewExpenseOcrModalPage />,
                },
              ],
              element: <MyGroupsListTab />,
            },
          ],
          element: (
            <PageDateFiltersProvider>
              <MyExpensesListPage />
            </PageDateFiltersProvider>
          ),
        },
        {
          path: navigation.myExpenses.groups.path,
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.breadcrumbs.my_expenses'),
            }),
          },
          children: [
            {
              index: true,
              loader: () => redirect(navigation.myExpenses.list.groups.fullPath()),
            },
            {
              path: navigation.myExpenses.groups.id.path,
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('expenses_v2.breadcrumbs.group_id'),
                }),
              },

              children: [
                {
                  path: navigation.myExpenses.groups.id.edit.path,
                  element: <GroupEditPage />,
                },
                {
                  path: navigation.myExpenses.groups.id.new.path,
                  element: <NewManualExpensesPage />,
                },
                {
                  path: navigation.myExpenses.groups.id.newMileage.path,
                  element: <NewMileagePage />,
                },
                {
                  path: navigation.myExpenses.groups.id.newPerDiem.path,
                  element: <NewPerDiemPage />,
                },
                {
                  path: navigation.myExpenses.groups.id.newExpenseOcr.path,
                  element: <NewExpenseOcrModalPage />,
                },
                {
                  path: navigation.myExpenses.groups.id.expenseId.path,
                  element: <MyGroupExpenseDetailPage />,
                },
              ],
              element: <MyGroupDetailPage />,
            },
          ],
          element: <WithDateFilters />,
        },
        {
          path: navigation.myExpenses.cards.path,
          loader: () => redirect(navigation.myCards.fullPath()),
          children: [
            {
              index: true,
              loader: () => redirect(navigation.myCards.fullPath()),
            },
            {
              path: navigation.myExpenses.cards.list.path,
              loader: () => redirect(navigation.myCards.fullPath()),
            },
            {
              path: navigation.myExpenses.cards.requestCard.path,
              loader: () => redirect(navigation.myCards.fullPath()),
            },
          ].filter(Boolean),
        },
      ],
    }),
  ]
})
